import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import type { Volume } from '@/service/gpu-instance/volume-type';
import { cn } from '@/lib/utils';

interface VolumeUsageChartProps {
  volume: Volume;
  className?: string;
}

export const VolumeUsageChart = ({ volume, className }: VolumeUsageChartProps) => {
  const usagePercent = Math.round((volume.usedSize / volume.requestedSize) * 100);
  const allocatedPercent = Math.round((volume.allocatedSize / volume.requestedSize) * 100);

  const usageStatus = useMemo(() => {
    if (usagePercent >= 90) return { color: 'bg-red-500', text: 'text-red-600', label: 'Critical' };
    if (usagePercent >= 75) return { color: 'bg-yellow-500', text: 'text-yellow-600', label: 'Warning' };
    return { color: 'bg-green-500', text: 'text-green-600', label: 'Healthy' };
  }, [usagePercent]);

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Storage Usage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Usage Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className={cn('text-2xl font-bold', usageStatus.text)}>
              {usagePercent}%
            </span>
            <span className="text-sm text-muted-foreground">
              {volume.usedSize} GB / {volume.requestedSize} GB
            </span>
          </div>
          <div className="relative">
            <Progress value={usagePercent} className="h-3" />
            <div
              className="absolute top-0 h-3 border-r-2 border-dashed border-muted-foreground/50"
              style={{ left: `${allocatedPercent}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Used: {volume.usedSize} GB</span>
            <span>Allocated: {volume.allocatedSize} GB</span>
            <span>Total: {volume.requestedSize} GB</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="rounded-md bg-muted/50 p-2">
            <p className="text-muted-foreground">Free Space</p>
            <p className="font-medium">{volume.requestedSize - volume.usedSize} GB</p>
          </div>
          <div className="rounded-md bg-muted/50 p-2">
            <p className="text-muted-foreground">Snapshots</p>
            <p className="font-medium">{volume.snapshotCount}</p>
          </div>
          <div className="rounded-md bg-muted/50 p-2">
            <p className="text-muted-foreground">Status</p>
            <p className={cn('font-medium', usageStatus.text)}>{usageStatus.label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VolumeUsageChart;
